import type { FC, ReactNode } from 'react'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { PageHeader } from '@/components/generals/page-header'
import { ContactDetails } from '@/components/contact/contact-details'
import { InquiryForm } from '@/components/contact/inquiry-form'
import { Faq } from '@/components/contact/faq'
import { SplitText } from '@/components/motion/split-text'
import { Reveal } from '@/components/motion/reveal'
import { RevealImage } from '@/components/motion/reveal-image'
import { contact } from '@/constants/site'

const Contact: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Contact'
                description='Book a portrait, wedding, family or event session with Northlight in Edinburgh. Enquiries answered within two working days.'
                ogImage='/images/cap-hat.jpg'
            />

            <PageHeader
                eyebrow='Contact'
                lines={['Let’s give it', 'a shot.']}
                lead='Whether you are ready to book a session or just have a few questions, I would love to hear from you. The more you can say about who is in the pictures and what they are for, the better the first reply will be.'
                image='/images/cap-hat.jpg'
                imageAlt='Portrait of a woman in a wide-brimmed hat photographed outdoors'
                meta={[
                    { label: 'Reply time', value: contact.response },
                    { label: 'Booking ahead', value: '3 to 6 months for weddings' },
                    { label: 'Travel', value: 'UK and international' }
                ]}
            />

            <ContactDetails />

            <section id='enquiry' className='edge bg-background py-20 md:py-28'>
                <div className='mx-auto grid max-w-[110rem] gap-12 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-16'>
                    <div className='lg:sticky lg:top-28 lg:self-start'>
                        <p className='label mb-8 text-muted-foreground'>Enquiries</p>

                        <SplitText as='h2' lines={['E-mail me at']} className='display-lg' />

                        <Reveal delay={0.1}>
                            <a
                                href={`mailto:${contact.email}`}
                                className='display-md mt-4 inline-block break-all text-[color:var(--gold-ink)] transition-opacity duration-500 hover:opacity-70'
                            >
                                {contact.email}
                            </a>

                            <p className='mt-6 max-w-xs text-[0.9375rem] leading-relaxed text-muted-foreground'>
                                Or simply leave your details below and I will come back to you
                                shortly. Four fields are required, the rest just save us a round
                                of emails.
                            </p>
                        </Reveal>

                        <RevealImage
                            src='/images/cap-bento-2.jpg'
                            alt='The photographer at work with a camera in hand'
                            className='mt-10 hidden aspect-[4/5] rounded-[var(--radius-lg)] lg:block'
                            sizes='30vw'
                            delay={0.18}
                            parallax={10}
                        />
                    </div>

                    <InquiryForm />
                </div>
            </section>

            <Faq />
        </Layout>
    )
}

export default Contact
